// src/pages/bloomterminal/config/chartRegistry.js
import PriceLineChart from '../components/charts/PriceLineChart';
import TerminalPriceLineChart from '../components/charts/TerminalPriceLineChart';
import TerminalPricePulseTable from '../components/charts/TerminalPricePulseTable';
import TerminalPricePulseTableContainer from '../components/charts/TerminalPricePulseTableContainer';
import ClusterChart from '../components/charts/ClusterChart';
import StackedBarChart from '../components/charts/StackedBarChart';
import PredictionsChart from '../components/charts/PredictionsChart';
import VolumeLineChart from '../components/charts/VolumeLineChart';
import ProducingRegionsMap from '../components/charts/ProducingRegionsMap';
import FOBPriceAvgChart from '../components/charts/FOBPriceAvgChart';
import FOBPriceHeatmap from '../components/charts/FOBPriceHeatmap';
import FOBPriceByOriginChart from '../components/charts/FOBPriceByOriginChart';
import FOBPriceByOriginChartContainer from '../components/charts/FOBPriceByOriginChartContainer';
import FOBSeasonalityChart from '../components/charts/FOBSeasonalityChart';
import FOBSeasonalityChartContainer from '../components/charts/FOBSeasonalityChartContainer';
import MarketShareAreaChart from '../components/charts/MarketShareAreaChart';
import MarketShareSummaryTable from '../components/charts/MarketShareSummaryTable';
import PriceClassifierChart from '../components/charts/PriceClassifierChart';
import PriceClassifierContainer from '../components/charts/PriceClassifierContainer';
import CargoVarietiesChart from '../components/charts/CargoVarietiesChart';
import CargoVarietiesChartContainer from '../components/charts/CargoVarietiesChartContainer';

// Import preview images
import pricePulsePreview from '../assets/chart-previews/price-pulse.png';
import terminalPriceTablePreview from '../assets/chart-previews/terminal-price-table.png';
import smartPriceTrackerPreview from '../assets/chart-previews/smart-price-tracker.png';
import priceClassifierPreview from '../assets/chart-previews/price-classifier.png';
import volumeByOriginPreview from '../assets/chart-previews/volume-by-origin.png';
import cropCasterPreview from '../assets/chart-previews/crop-caster.png';
import commodityPlannerPreview from '../assets/chart-previews/commodity-planner.png';
import producingRegionsPreview from '../assets/chart-previews/producing-regions.png';
import fobPriceAvgPreview from '../assets/chart-previews/fob-price-avg.png';
import fobPriceByOriginPreview from '../assets/chart-previews/fob-price-by-origin.png';
import fobPriceHeatmapPreview from '../assets/chart-previews/fob-price-heatmap.png';
import fobSeasonalityPreview from '../assets/chart-previews/fob-seasonality.png';
import marketShareAreaPreview from '../assets/chart-previews/market-share-area.png';
import marketShareSummaryPreview from '../assets/chart-previews/market-share-summary.png';
import cargoVarietiesPreview from '../assets/chart-previews/cargo-varieties.png';

// Import data transformation utilities
import {
  preparePricesData,
  prepareStackedBarData,
  prepareLineData,
  preparePredictionsData,
  prepareClusterData,
  prepareProducingRegionsData,
  prepareFobPriceAvgData,
  prepareFobPriceByOriginData,
  prepareFobSeasonalityData,
  prepareMarketShareChartData,
  prepareMarketShareSummaryData,
} from '../utils/analyticsHelpers';

// Import API hooks
import {
  usePriceChartData,
  useTerminalPriceChartData,
  useDailyVolumeChartData,
  useHistoricVolumeChartData,
  usePredictionsChartData,
  useClusterChartData,
  useProducingRegionsData,
  useFobPriceAvgData,
  useFobPriceByOriginData,
  useFobSeasonalityData,
  useMarketShareChartData,
  useMarketShareSummaryData,
} from '../../utils/api-hooks/analytics';

// Import HOC for data fetching
import withChartData from '../components/dashboard/withChartData';

/**
 * Chart Registry - Central metadata for all available dashboard widgets
 *
 * Each chart entry contains:
 * - id: Unique identifier used in layouts and state
 * - titleKey: Translation key for display name
 * - descriptionKey: Translation key for description
 * - component: Raw chart component (for reference)
 * - containerComponent: Chart wrapped with data fetching (rendered by WidgetContainer)
 * - tags: Searchable tags for filtering
 * - defaultSize: Default grid size { w, h }
 * - minSize: Minimum resize dimensions { w, h }
 * - previewImage: Preview image for add widget popup
 */
export const CHART_REGISTRY = {
  'price-line': {
    id: 'price-line',
    titleKey: 'charts.pricePulse.title',
    descriptionKey: 'charts.pricePulse.description',
    component: PriceLineChart,
    containerComponent: withChartData(PriceLineChart, usePriceChartData, preparePricesData, 'priceChart'),
    tags: ['price', 'trends', 'line', 'district', 'market', 'daily'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 6, h: 4 },
    previewImage: pricePulsePreview,
  },
  'terminal-price-line': {
    id: 'terminal-price-line',
    titleKey: 'charts.terminalPricePulse.title',
    descriptionKey: 'charts.terminalPricePulse.description',
    component: TerminalPriceLineChart,
    containerComponent: withChartData(TerminalPriceLineChart, useTerminalPriceChartData, preparePricesData, 'priceChart'),
    tags: ['price', 'terminal', 'trends', 'line', 'district', 'market', 'daily'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 6, h: 4 },
    previewImage: pricePulsePreview, // Using same preview image for now
  },
  'terminal-price-pulse': {
    id: 'terminal-price-pulse',
    titleKey: 'charts.terminalPricePulseTable.title',
    descriptionKey: 'charts.terminalPricePulseTable.description',
    component: TerminalPricePulseTable,
    containerComponent: TerminalPricePulseTableContainer,
    tags: ['terminal', 'price', 'pulse', 'delta', 'fob', 'table', 'weekly'],
    defaultSize: { w: 48, h: 20 },
    minSize: { w: 24, h: 18 },
    previewImage: terminalPriceTablePreview,
  },
  'cluster': {
    id: 'cluster',
    titleKey: 'charts.smartPriceTracker.title',
    descriptionKey: 'charts.smartPriceTracker.description',
    component: ClusterChart,
    containerComponent: withChartData(ClusterChart, useClusterChartData, prepareClusterData, 'clusterChart'),
    tags: ['price', 'cluster', 'weekly', 'category', 'smart', 'tracker'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 6, h: 4 },
    previewImage: smartPriceTrackerPreview,
  },
  'stacked-bar': {
    id: 'stacked-bar',
    titleKey: 'charts.volumeByOrigin.title',
    descriptionKey: 'charts.volumeByOrigin.description',
    component: StackedBarChart,
    containerComponent: withChartData(StackedBarChart, useDailyVolumeChartData, prepareStackedBarData, 'volumeChart'),
    tags: ['volume', 'origin', 'region', 'bar', 'stacked', 'daily'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 6, h: 4 },
    previewImage: volumeByOriginPreview,
  },
  'predictions': {
    id: 'predictions',
    titleKey: 'charts.cropCaster.title',
    descriptionKey: 'charts.cropCaster.description',
    component: PredictionsChart,
    containerComponent: withChartData(PredictionsChart, usePredictionsChartData, preparePredictionsData, 'predictionsChart'),
    tags: ['predictions', 'season', 'forecast', 'crop', 'gauge', 'future'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 6, h: 4 },
    previewImage: cropCasterPreview,
  },
  'volume-line': {
    id: 'volume-line',
    titleKey: 'charts.commodityPlanner.title',
    descriptionKey: 'charts.commodityPlanner.description',
    component: VolumeLineChart,
    containerComponent: withChartData(VolumeLineChart, useHistoricVolumeChartData, prepareLineData, 'historicVolumeChart'),
    tags: ['volume', 'historic', 'planner', 'area', 'line', 'trends'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 6, h: 4 },
    previewImage: commodityPlannerPreview,
  },
  'producing-regions': {
    id: 'producing-regions',
    titleKey: 'charts.producingRegions.title',
    descriptionKey: 'charts.producingRegions.description',
    component: ProducingRegionsMap,
    containerComponent: withChartData(ProducingRegionsMap, useProducingRegionsData, prepareProducingRegionsData, 'producingRegions'),
    tags: ['map', 'world', 'regions', 'producing', 'harvest', 'global', 'location', 'geography', 'varieties'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 6, h: 4 },
    previewImage: producingRegionsPreview,
  },
  'fob-price-avg': {
    id: 'fob-price-avg',
    titleKey: 'charts.fobPriceAvg.title',
    descriptionKey: 'charts.fobPriceAvg.description',
    component: FOBPriceAvgChart,
    containerComponent: withChartData(FOBPriceAvgChart, useFobPriceAvgData, prepareFobPriceAvgData, 'fobPriceAvg'),
    tags: ['price', 'fob', 'trends', 'line', 'yoy', 'year-over-year', 'average', 'comparison'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 8, h: 8 },
    previewImage: fobPriceAvgPreview,
  },
  'market-share': {
    id: 'market-share',
    titleKey: 'charts.marketShare.title',
    descriptionKey: 'charts.marketShare.description',
    component: MarketShareAreaChart,
    containerComponent: withChartData(MarketShareAreaChart, useMarketShareChartData, prepareMarketShareChartData, 'marketShareData'),
    tags: ['market', 'share', 'volume', 'origin', 'area', 'stacked', 'distribution', 'percentage'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 8, h: 8 },
    previewImage: marketShareAreaPreview,
  },
  'fob-price-heatmap': {
    id: 'fob-price-heatmap',
    titleKey: 'charts.fobPriceHeatmap.title',
    descriptionKey: 'charts.fobPriceHeatmap.description',
    component: FOBPriceHeatmap,
    containerComponent: withChartData(FOBPriceHeatmap, useFobPriceByOriginData, prepareFobPriceByOriginData, 'fobPriceByOrigin'),
    tags: ['price', 'fob', 'heatmap', 'origin', 'comparison', 'grid', 'delta', 'change'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 12, h: 16 },
    previewImage: fobPriceHeatmapPreview,
  },
  'fob-price-by-origin': {
    id: 'fob-price-by-origin',
    titleKey: 'charts.fobPriceByOrigin.title',
    descriptionKey: 'charts.fobPriceByOrigin.description',
    component: FOBPriceByOriginChart,
    containerComponent: FOBPriceByOriginChartContainer,
    tags: ['price', 'fob', 'origin', 'line', 'trends'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 12, h: 16 },
    previewImage: fobPriceByOriginPreview,
  },
  'fob-seasonality': {
    id: 'fob-seasonality',
    titleKey: 'charts.fobSeasonality.title',
    descriptionKey: 'charts.fobSeasonality.description',
    component: FOBSeasonalityChart,
    containerComponent: FOBSeasonalityChartContainer,
    tags: ['seasonality', 'fob', 'origin', 'shipping', 'volume', 'calendar'],
    defaultSize: { w: 24, h: 16 },
    minSize: { w: 8, h: 5 },
    previewImage: fobSeasonalityPreview,
  },
  'market-share-summary': {
    id: 'market-share-summary',
    titleKey: 'charts.marketShareSummary.title',
    descriptionKey: 'charts.marketShareSummary.description',
    component: MarketShareSummaryTable,
    containerComponent: withChartData(MarketShareSummaryTable, useMarketShareSummaryData, prepareMarketShareSummaryData, 'summaryData'),
    tags: ['market', 'share', 'summary', 'table', 'comparison', 'delta', 'trend'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 12, h: 16 },
    previewImage: marketShareSummaryPreview,
  },
  'price-classifier': {
    id: 'price-classifier',
    titleKey: 'charts.smartPriceClassifier.title',
    descriptionKey: 'charts.smartPriceClassifier.description',
    component: PriceClassifierChart,
    containerComponent: PriceClassifierContainer,
    tags: ['price', 'classifier', 'smart', 'tier', 'weekly', 'ml', 'interactive'],
    defaultSize: { w: 24, h: 5 },
    minSize: { w: 8, h: 6 },
    previewImage: priceClassifierPreview,
  },
  'cargo-varieties': {
    id: 'cargo-varieties',
    titleKey: 'charts.cargoVarieties.title',
    descriptionKey: 'charts.cargoVarieties.description',
    component: CargoVarietiesChart,
    containerComponent: CargoVarietiesChartContainer,
    tags: ['cargo', 'varieties', 'butterfly', 'coast', 'ports', 'peru', 'shipments', 'arrivals'],
    defaultSize: { w: 24, h: 24 },
    minSize: { w: 12, h: 8 },
    previewImage: cargoVarietiesPreview,
  },
};

/**
 * Get all chart IDs
 */
export const getAllChartIds = () => Object.keys(CHART_REGISTRY);

/**
 * Get chart config by ID
 */
export const getChartConfig = (chartId) => CHART_REGISTRY[chartId];

/**
 * Get all charts as array (useful for rendering lists)
 */
export const getAllCharts = () => Object.values(CHART_REGISTRY);

/**
 * Default layout configuration for new users
 * Using 48-column grid with 20px row height for fine-grained resizing
 */
export const DEFAULT_LAYOUT = {
  activeWidgets: ['price-line', 'cluster', 'stacked-bar', 'predictions', 'volume-line'],
  layouts: {
    lg: [
      { i: 'price-line', x: 0, y: 0, w: 24, h: 24 },
      { i: 'cluster', x: 24, y: 0, w: 24, h: 24 },
      { i: 'stacked-bar', x: 0, y: 24, w: 24, h: 24 },
      { i: 'predictions', x: 24, y: 24, w: 24, h: 24 },
      { i: 'volume-line', x: 0, y: 48, w: 48, h: 24 },
      { i: 'cargo-varieties', x: 0, y: 72, w: 24, h: 28 },
    ],
    md: [
      { i: 'price-line', x: 0, y: 0, w: 20, h: 24 },
      { i: 'cluster', x: 20, y: 0, w: 20, h: 24 },
      { i: 'stacked-bar', x: 0, y: 24, w: 20, h: 24 },
      { i: 'predictions', x: 20, y: 24, w: 20, h: 24 },
      { i: 'volume-line', x: 0, y: 48, w: 40, h: 24 },
      { i: 'cargo-varieties', x: 0, y: 72, w: 20, h: 28 },
    ],
    sm: [
      { i: 'price-line', x: 0, y: 0, w: 24, h: 24 },
      { i: 'cluster', x: 0, y: 24, w: 24, h: 24 },
      { i: 'stacked-bar', x: 0, y: 48, w: 24, h: 24 },
      { i: 'predictions', x: 0, y: 72, w: 24, h: 24 },
      { i: 'volume-line', x: 0, y: 96, w: 24, h: 24 },
      { i: 'cargo-varieties', x: 0, y: 120, w: 24, h: 28 },
    ],
    xs: [
      { i: 'price-line', x: 0, y: 0, w: 16, h: 24 },
      { i: 'cluster', x: 0, y: 24, w: 16, h: 24 },
      { i: 'stacked-bar', x: 0, y: 48, w: 16, h: 24 },
      { i: 'predictions', x: 0, y: 72, w: 16, h: 24 },
      { i: 'volume-line', x: 0, y: 96, w: 16, h: 24 },
      { i: 'cargo-varieties', x: 0, y: 120, w: 16, h: 28 },
    ],
    xxs: [
      { i: 'price-line', x: 0, y: 0, w: 8, h: 24 },
      { i: 'cluster', x: 0, y: 24, w: 8, h: 24 },
      { i: 'stacked-bar', x: 0, y: 48, w: 8, h: 24 },
      { i: 'predictions', x: 0, y: 72, w: 8, h: 24 },
      { i: 'volume-line', x: 0, y: 96, w: 8, h: 24 },
      { i: 'cargo-varieties', x: 0, y: 120, w: 8, h: 28 },
    ],
  },
};

/**
 * Check if a chart is restricted for a given tier using Firebase config
 */
export const isChartRestricted = (chartId, tier, chartRestrictions) => {
  if (!chartRestrictions || !tier) return false;
  const restricted = chartRestrictions[tier] || [];
  return restricted.includes(chartId);
};

export default CHART_REGISTRY;
